import { queryOptions, useQuery } from "@tanstack/react-query";
import { ACTIVE_CATEGORIES, isActiveServiceCategory } from "constants/categories";
import { getAdCategoriesRequest, type AdCategoryReference } from "lib/api/reference/adCategory";

export const adCategoryReferenceKeys = {
    all: ["reference", "ad_category"] as const,
};

const fallbackAdCategories = ACTIVE_CATEGORIES.map((category) => ({
    value: category.key,
    subcategories: category.values.map((subcategory) => ({ value: subcategory })),
}));

const toActiveAdCategories = (payload: Array<AdCategoryReference>): Array<AdCategoryReference> =>
    payload.filter((category) => isActiveServiceCategory(category.value));

const isAdCategoryReferencePayload = (payload: unknown): payload is Array<AdCategoryReference> =>
    Array.isArray(payload);

const isWrappedAdCategoryReferencePayload = (payload: unknown): payload is { data: Array<AdCategoryReference> } =>
    typeof payload === "object" &&
    payload !== null &&
    "data" in payload &&
    isAdCategoryReferencePayload(payload.data);

export const fetchAdCategories = async (): Promise<Array<AdCategoryReference>> => {
    try {
        const response = await getAdCategoriesRequest();
        const payload = response.data;

        if (isAdCategoryReferencePayload(payload)) {
            return toActiveAdCategories(payload);
        }

        if (isWrappedAdCategoryReferencePayload(payload)) {
            return toActiveAdCategories(payload.data);
        }

        return fallbackAdCategories;
    } catch {
        return fallbackAdCategories;
    }
};

export const adCategoryReferenceQueryOptions = () =>
    queryOptions<Array<AdCategoryReference>>({
        queryKey: adCategoryReferenceKeys.all,
        queryFn: fetchAdCategories,
        initialData: fallbackAdCategories,
        staleTime: Number.POSITIVE_INFINITY,
    });

export const useAdCategories = () => useQuery(adCategoryReferenceQueryOptions());
